'use client'

import { type DependencyList, type RefObject, useEffect } from 'react'

const TEXT_BLOCKS = 'h1,h2,h3,h4,h5,h6,p,ul,ol,[data-text-block]'

/**
 * Makes every heading, paragraph and list inside `ref` a keyboard tab stop with the shared
 * `.block-focus` ring, so keyboard users can step through long text content block by block.
 * Blocks nested in another block (e.g. the <p>s inside a `data-text-block` info box) and text
 * inside buttons, links or labels are skipped. A MutationObserver picks up content that renders
 * later, e.g. when a dialog switches views.
 */
export function useFocusableTextBlocks(
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList = []
) {
  useEffect(() => {
    const root = ref.current
    if (!root) return

    const mark = () => {
      root.querySelectorAll<HTMLElement>(TEXT_BLOCKS).forEach((el) => {
        if (el.hasAttribute('tabindex')) return
        const outer = el.parentElement?.closest(TEXT_BLOCKS)
        if (outer && root.contains(outer)) return
        if (el.closest('button,a,label')) return
        el.setAttribute('tabindex', '0')
        el.classList.add('block-focus')
      })
    }

    mark()
    const observer = new MutationObserver(mark)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
